import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import CuttingCard from '../components/CuttingCard';
import DisplayModeToggle from '../components/DisplayModeToggle';

import { LABEL_MODES, useLabelMode, getLabelText } from '../hooks/useLabelMode';
export default function CuttingsPage() {
  const { t } = useLang();
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  const [cuttings, setCuttings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('name');
  const [labelMode, setLabelMode] = useLabelMode();
  const [mode, setMode] = useState('medium');
  const [group, setGroup] = useState(false);
  const [open, setOpen] = useState({});

  const downloadAllPdf = async () => {
    try {
      const params = new URLSearchParams({ labelMode });
      const res = await fetch(`/api/qr/pdf/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cuttings_qr_${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF', err);
    }
  };

  const downloadExcel = async () => {
    try {
      const res = await fetch('/api/qr/excel/all', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cuttings_qr_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download Excel', err);
    }
  };

  const limit = 20;

  const fetchCuttings = async (s = 0, replace = false) => {
    try {
      const res = await fetch(`/api/cuttings?skip=${s}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const arr = Array.isArray(data) ? data : [];
      setCuttings((prev) => (replace ? arr : [...prev, ...arr]));
      setSkip(s + arr.length);
      setHasMore(arr.length === limit);
    } catch (err) {
      console.error('Failed to fetch cuttings', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCuttings(0, true);
  }, []);

  const filtered = cuttings
    .filter(
      (c) =>
        (c.strainName || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.strainCode || c.strain || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.code || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.shortId || '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sort === 'name')
        return (a.strainName || '').localeCompare(b.strainName || '');
      return (a.strainCode || a.strain || '').localeCompare(
        b.strainCode || b.strain || ''
      );
    });

  if (loading) {
    return (
      <div className="container mt-3">
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="container mt-3">
      <h1 className="mb-3">{t('nav.cuttings')}</h1>
      <div className="row g-2 mb-3 align-items-center">
        <div className="col-12 col-md">
          <input
            className="form-control"
            placeholder={t('search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-6 col-md-auto">
          <select
            className="form-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="name">{t('byName')}</option>
            <option value="code">{t('byCode')}</option>
          </select>
        </div>
        <div className="col-6 col-md-auto">
          <select
            className="form-select"
            value={labelMode}
            onChange={(e) => setLabelMode(e.target.value)}
          >
            <option value={LABEL_MODES.NAME_ONLY}>{t('labelModes.nameOnly')}</option>
            <option value={LABEL_MODES.NUMBER_ONLY}>{t('labelModes.numberOnly')}</option>
            <option value={LABEL_MODES.NAME_AND_NUMBER}>{t('labelModes.nameAndNumber')}</option>
          </select>
        </div>
        <div className="col-12 col-md-auto">
          <DisplayModeToggle mode={mode} setMode={setMode} />
        </div>
        <div className="col-12 col-md-auto">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="group"
              checked={group}
              onChange={(e) => setGroup(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="group">
              {t('strain')}
            </label>
          </div>
        </div>
        <div className="col-12">
          <div className="d-flex flex-column flex-md-row gap-2">
            <button
              className="btn btn-outline-secondary w-100"
              onClick={downloadAllPdf}
            >
              📄 {t('downloadAllQrPdf')}
            </button>
            <button
              className="btn btn-outline-secondary w-100"
              onClick={downloadExcel}
            >
              📊 {t('downloadExcel')}
            </button>
          </div>
        </div>
      </div>
      {cuttings.length === 0 ? (
        <p>{t('noCuttings') || 'No cuttings yet'}</p>
      ) : group ? (
        Object.entries(
          filtered.reduce((acc, c) => {
            const key = c.strainName || c.strain;
            if (!acc[key]) acc[key] = [];
            acc[key].push(c);
            return acc;
          }, {})
        ).map(([strain, list]) => (
          <div key={strain} className="mb-3">
            <button
              className="btn btn-link"
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [strain]: !o[strain] }))}
            >
              {open[strain] ? '➖' : '➕'} {strain}
            </button>
            {open[strain] && (
              mode === 'table' ? (
                <div className="table-responsive">
                  <table className="table">
                    <tbody>
                      {list.map((c) => (
                        <tr key={c._id}>
                          <td>{c.shortId || c._id}</td>
                          <td>
                            <span
                              className="d-inline-block text-truncate"
                              style={{ maxWidth: 220, wordBreak: 'break-word' }}
                              title={getLabelText(labelMode, c.strainName || c.strain, c.code, ' ')}
                            >
                              {getLabelText(labelMode, c.strainName || c.strain, c.code, ' ')}
                            </span>
                          </td>
                          <td>{c.status || '-'}</td>
                          <td>{c.location || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="row">
                  {list.map((c) => (
                    <div
                      key={c._id}
                      className={`${
                        mode === 'large'
                          ? 'col-12 col-md-6'
                          : 'col-12 col-md-6 col-lg-4'
                      } mb-3`}
                    >
                      <CuttingCard cutting={c} labelMode={labelMode} />
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        ))
      ) : mode === 'table' ? (
        <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>{t('id')}</th>
              <th>{t('strain')}</th>
              <th>{t('status')}</th>
              <th>{t('location')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c._id}>
                <td>{c.shortId || c._id}</td>
                <td>
                  <span
                    className="d-inline-block text-truncate"
                    style={{ maxWidth: 220, wordBreak: 'break-word' }}
                    title={getLabelText(labelMode, c.strainName || c.strain, c.code, ' ')}
                  >
                    {getLabelText(labelMode, c.strainName || c.strain, c.code, ' ')}
                  </span>
                </td>
                <td>{c.status || '-'}</td>
                <td>{c.location || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <div className="row">
          {filtered.map((c) => (
            <div
              key={c._id}
              className={`${
                mode === 'large' ? 'col-12 col-md-6' : 'col-12 col-md-6 col-lg-4'
              } mb-3`}
            >
              <CuttingCard cutting={c} labelMode={labelMode} />
            </div>
          ))}
        </div>
      )}
      {hasMore && (
        <div className="text-center mt-3">
          <button className="btn btn-outline-secondary" onClick={() => fetchCuttings(skip)}>
            {t('viewMore')}
          </button>
        </div>
      )}
    </div>
  );
}
